import { createFileRoute, Outlet, Link, redirect } from "@tanstack/react-router";
import { AuthGate } from "@/components/AuthGate";
import { useIsAdmin } from "@/hooks/use-is-admin";
import { isCurrentUserAdmin } from "@/lib/kg.admin.functions";
import { PageContainer } from "@/components/PageContainer";
import { Eyebrow, DisplayHeading } from "@/components/editorial";

export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: "Administrator — Tiger's Eye" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AuthGate>
      <AdminLayout />
    </AuthGate>
  ),
});

function AdminLayout() {
  const { isAdmin, email, loading } = useIsAdmin();

  if (loading) {
    return (
      <PageContainer className="min-h-[60vh]">
        <p className="text-ink-tertiary font-serif">Checking access…</p>
      </PageContainer>
    );
  }

  if (!isAdmin) {
    return (
      <PageContainer className="min-h-[60vh]">
        <Eyebrow className="mb-3">Restricted</Eyebrow>
        <DisplayHeading level={1} className="mb-4">Administrator only</DisplayHeading>
        <p className="text-ink-secondary mb-2">
          This area is limited to addresses on the <span className="font-mono">ADMIN_EMAILS</span> allowlist.
        </p>
        {email && (
          <p className="text-sm text-ink-tertiary">
            Signed in as <span className="font-mono">{email}</span>.
          </p>
        )}
      </PageContainer>
    );
  }

  return (
    <PageContainer className="min-h-[60vh]">
      <div className="flex items-baseline justify-between mb-8 border-b border-rule pb-6">
        <div>
          <Eyebrow>Administrator</Eyebrow>
          <DisplayHeading level={1} className="mt-1">Tiger&apos;s Eye</DisplayHeading>
        </div>
        {email && (
          <p className="text-xs font-mono text-ink-tertiary">{email}</p>
        )}
      </div>

      <nav className="flex flex-wrap gap-2 mb-10 text-sm">
        <AdminTab to="/admin">Overview</AdminTab>
        <AdminTab to="/admin/users">Users</AdminTab>
        <AdminTab to="/admin/reports">Reports</AdminTab>
        <AdminTab to="/admin/landing">Landing A/B</AdminTab>
        <AdminTab to="/admin/team">Team &amp; roles</AdminTab>
        <AdminTab to="/admin/audit">Audit log</AdminTab>
        <AdminTab to="/admin/kg">Knowledge graph</AdminTab>
        <AdminTab to="/admin/references">References</AdminTab>
        <AdminTab to="/admin/docs">Docs</AdminTab>
        <AdminTab to="/admin/debug">Debug</AdminTab>
        <AdminTab to="/admin/dev/progress-mockup">Dev</AdminTab>
        <AdminTab to="/admin/institutions">Institutions</AdminTab>
        <AdminTab to="/admin/policy-links">Policy links</AdminTab>
        <AdminTab to="/admin/change-requests">Change requests</AdminTab>
        <AdminTab to="/admin/signals">Signals</AdminTab>
        <AdminTab to="/admin/patterns">Patterns</AdminTab>
        <AdminTab to="/admin/calibration">Calibration</AdminTab>
        <AdminTab to="/admin/ai-provider">AI provider</AdminTab>
        <AdminTab to="/admin/ai-benchmark">AI benchmark</AdminTab>
        <AdminTab to="/admin/codes">Free codes</AdminTab>


      </nav>

      <Outlet />
    </PageContainer>
  );
}

function AdminTab({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      activeOptions={{ exact: to === "/admin" }}
      activeProps={{ className: "bg-primary text-primary-foreground border-primary" }}
      className="rounded-full px-4 py-2 border border-rule hover:bg-surface transition-colors"
    >
      {children}
    </Link>
  );
}
